import React, { useState, useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import FormInput from '../../../components/FormInput';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Country, State, City } from 'country-state-city';
import { FaEye, FaLock, FaUser, FaMapMarkerAlt, FaFileAlt, FaBuilding } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const UserManage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useContext(AuthContext);

  const isViewMode = location.state?.viewMode || false;
  const isAddMode = id === 'new';
  const isEditMode = !isAddMode && !isViewMode && id;

  const [isLoading, setIsLoading] = useState(false);
  const { data: user, loading, fetchData } = useApi(id && !isAddMode ? `/users/${id}` : null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    password: '',
    gender: '',
    address: '',
    phone: '',
    postalCode: '',
    street: '',
    country: '',
    state: '',
    city: '',
    active: true,
    isVerified: false,
    documentType: '',
    documents: [],
    profilePicture: null,
    fees: 0,
    availability: '',
    bio: '',
    specialties: [],
    experience: 0,
    timeSlots: [],
    userType: 'individual',
    companyName: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmNewPassword: '',
  });
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);

  useEffect(() => {
    if (user && !isAddMode) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: user.role || '',
        password: '',
        gender: user.gender || '',
        address: user.address || '',
        phone: user.phone || '',
        postalCode: user.postalCode || '',
        street: user.street || '',
        country: user.country || '',
        state: user.state || '',
        city: user.city || '',
        active: user.active ?? true,
        isVerified: user.isVerified ?? false,
        documentType: '',
        documents: user.verificationDocuments || [],
        profilePicture: null,
        fees: user.fees || 0,
        availability: user.availability || '',
        bio: user.bio || '',
        specialties: user.specialties || [],
        experience: user.experience || 0,
        timeSlots: user.timeSlots || [],
        userType: user.userType || 'individual',
        companyName: user.companyName || '',
      });
    } else if (isAddMode) {
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        password: '',
        gender: '',
        address: '',
        phone: '',
        postalCode: '',
        street: '',
        country: '',
        state: '',
        city: '',
        active: true,
        isVerified: false,
        documentType: '',
        documents: [],
        profilePicture: null,
        fees: 0,
        availability: '',
        bio: '',
        specialties: [],
        experience: 0,
        timeSlots: [],
        userType: 'individual',
        companyName: '',
      });
    }
  }, [user, isAddMode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : type === 'file' ? (name === 'documents' ? Array.from(files) : files[0]) : value,
    });
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({ ...passwordForm, [name]: value });
  };

  const handlePhoneChange = (value) => {
    setForm({ ...form, phone: value });
  };

  const handleRoleChange = (selected) => {
    const newRole = selected ? selected.value : '';
    setForm({
      ...form,
      role: newRole,
      ...(newRole !== 'specialist' && {
        fees: 0,
        availability: '',
        bio: '',
        specialties: [],
        experience: 0,
        timeSlots: [],
      }),
    });
  };

  const handleUserTypeChange = (selected) => {
    const newType = selected?.value || 'individual';
    setForm({
      ...form,
      userType: newType,
      ...(newType === 'company' && { firstName: '', lastName: '' }),
      ...(newType === 'individual' && { companyName: '' }),
    });
  };

  const handleGenderChange = (selected) => {
    setForm({ ...form, gender: selected ? selected.value : '' });
  };

  const handleCountryChange = (selected) => {
    setForm({ ...form, country: selected?.value || '', state: '', city: '' });
  };

  const handleStateChange = (selected) => {
    setForm({ ...form, state: selected?.value || '', city: '' });
  };

  const handleCityChange = (selected) => {
    setForm({ ...form, city: selected?.value || '' });
  };

  const handleDocumentTypeChange = (selected) => {
    setForm({ ...form, documentType: selected?.value || '' });
  };

  const handleAvailabilityChange = (selected) => {
    setForm({ ...form, availability: selected?.value || '' });
  };

  const handleSpecialtyChange = (selected) => {
    if (selected.length > 5) {
      toast.error('Maximum 5 specialties allowed.');
      return;
    }
    setForm({ ...form, specialties: selected.map((opt) => opt.value) });
  };

  const handleTimeSlotsChange = (selected) => {
    setForm({ ...form, timeSlots: selected.map((opt) => opt.value) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Basic validation for names
      if (form.role === 'user') {
        if (form.userType === 'company') {
          if (!form.companyName?.trim()) {
            toast.error('Company name is required');
            setIsLoading(false);
            return;
          }
        } else {
          if (!form.firstName?.trim() || !form.lastName?.trim()) {
            toast.error('First name and last name are required');
            setIsLoading(false);
            return;
          }
        }
      }

      const formData = new FormData();

      // Handle name fields based on userType
      let submitFirstName = form.firstName;
      let submitLastName = form.lastName;

      if (form.role === 'user' && form.userType === 'company') {
        submitFirstName = form.companyName.trim();
        submitLastName = '';
      }

      formData.append('firstName', submitFirstName);
      formData.append('lastName', submitLastName);
      formData.append('companyName', form.companyName || '');
      formData.append('userType', form.userType);

      // Other fields
      Object.entries(form).forEach(([key, value]) => {
        if (['firstName', 'lastName', 'companyName', 'userType'].includes(key)) return;

        if (key === 'specialties' && Array.isArray(value)) {
          value.forEach((specialty, index) => formData.append(`specialties[${index}]`, specialty));
        } else if (key === 'timeSlots' && Array.isArray(value)) {
          value.forEach((slot, index) => formData.append(`timeSlots[${index}]`, slot));
        } else if (key === 'documents' && Array.isArray(value) && value.length > 0) {
          value.forEach((doc) => formData.append('documents', doc));
          formData.append('documentTypes', form.documentType || 'certificate');
        } else if (key === 'profilePicture' && value) {
          formData.append('profilePicture', value);
        } else if (key === 'password' && isAddMode && value) {
          formData.append('password', value);
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      const endpoint = isAddMode ? '/users' : `/users/${id}`;
      const method = isAddMode ? 'POST' : 'PUT';

      await apiService.upload(endpoint, formData, { method });

      toast.success(isAddMode ? 'User added successfully' : 'User updated successfully');
      navigate('/users');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save user';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      toast.error('New password and confirm password do not match.');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long.');
      return;
    }

    setIsPasswordLoading(true);
    try {
      await apiService.post(`/users/admin/change-password/${id}`, {
        newPassword: passwordForm.newPassword,
      });

      toast.success('Password updated successfully');
      setPasswordForm({ newPassword: '', confirmNewPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to update password');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleViewProfilePicture = async (userId, url) => {
    try {
      const { blob, contentType } = await apiService.getBlob(`/users/document/${userId}/${url.split('/').pop()}`);
      if (!contentType.match(/image\/(jpeg|png)/)) {
        throw new Error('Unsupported file format.');
      }
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      toast.error(err.message || 'Failed to load file');
    }
  };

  const countryOptions = Country.getAllCountries().map((c) => ({ value: c.isoCode, label: c.name }));
  const stateOptions = form.country ? State.getStatesOfCountry(form.country).map((s) => ({ value: s.isoCode, label: s.name })) : [];
  const cityOptions = form.country && form.state ? City.getCitiesOfState(form.country, form.state).map((c) => ({ value: c.name, label: c.name })) : [];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];

  const roleOptions = [
    { value: 'farmer', label: 'Farmer' },
    { value: 'specialist', label: 'Specialist' },
    { value: 'waste_collector', label: 'Waste Collector' },
    { value: 'admin', label: 'Admin' },
    { value: 'user', label: 'User' },
  ];

  const availabilityOptions = [
    { value: 'available', label: 'Available' },
    { value: 'not_available', label: 'Not Available' },
  ];

  const specialtyOptions = [
    { value: 'poultry_care', label: 'Poultry Care' },
    { value: 'cattle_health', label: 'Cattle Health' },
    { value: 'veterinary_surgery', label: 'Veterinary Surgery' },
    { value: 'animal_nutrition', label: 'Animal Nutrition' },
    { value: 'livestock_management', label: 'Livestock Management' },
    { value: 'disease_control', label: 'Disease Control' },
    { value: 'farm_vaccination', label: 'Farm Vaccination' },
    { value: 'poultry_diagnostics', label: 'Poultry Diagnostics' },
    { value: 'avian_diseases', label: 'Avian Diseases Specialist' },
    { value: 'hatchery_management', label: 'Hatchery Management' },
    { value: 'broiler_management', label: 'Broiler Management' },
    { value: 'layer_management', label: 'Layer Farm Management' },
    { value: 'biosecurity_management', label: 'Biosecurity Management' },
    { value: 'feed_formulation', label: 'Feed Formulation' },
    { value: 'parasite_control', label: 'Parasite Control' },
    { value: 'antibiotic_stewardship', label: 'Antibiotic Stewardship' },
    { value: 'reproductive_health', label: 'Reproductive Health' },
    { value: 'egg_quality_management', label: 'Egg Quality Management' },
    { value: 'farm_sanitation', label: 'Farm Sanitation & Hygiene' },
    { value: 'environmental_management', label: 'Poultry Environment Management' },
    { value: 'heat_stress_management', label: 'Heat Stress Management' },
    { value: 'poultry_welfare', label: 'Animal Welfare (Poultry)' },
    { value: 'veterinary_consultation', label: 'General Veterinary Consultation' },
  ];

  const timeSlotOptions = [
    { value: '08:00-10:00', label: '08:00 AM - 10:00 AM' },
    { value: '10:00-12:00', label: '10:00 AM - 12:00 PM' },
    { value: '12:00-14:00', label: '12:00 PM - 02:00 PM' },
    { value: '14:00-16:00', label: '02:00 PM - 04:00 PM' },
    { value: '16:00-18:00', label: '04:00 PM - 06:00 PM' },
    { value: '18:00-20:00', label: '06:00 PM - 08:00 PM' },
  ];

  const documentTypeOptions = [
    { value: 'certificate', label: 'Certificate' },
    { value: 'license', label: 'License' },
    { value: 'farm_ownership', label: 'Farm Ownership' },
  ];

  const userTypeOptions = [
    { value: 'individual', label: 'Individual' },
    { value: 'company', label: 'Company' },
  ];

  const getDisplayName = () => {
    if (form.userType === 'company' && form.companyName) return form.companyName;
    return `${form.firstName || ''} ${form.lastName || ''}`.trim() || 'No name';
  };

  if (loading && !isAddMode) return <div className="text-center py-10">Loading...</div>;

  return (
    <div className="container mx-auto px-6 py-8 max-w-7xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-xl min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8 flex items-center gap-2">
        <FaUser className="text-blue-600 dark:text-blue-400" />
        {isAddMode ? 'Add New User' : isEditMode ? 'Edit User' : 'View User'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FaUser className="text-blue-500" /> Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleInputChange}
              required
              placeholder="Enter email"
              disabled={isViewMode}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Role</label>
              <Select
                options={roleOptions}
                value={roleOptions.find((opt) => opt.value === form.role) || null}
                onChange={handleRoleChange}
                placeholder="Select a role"
                isClearable
                isDisabled={isViewMode}
                className="w-full"
                classNamePrefix="react-select"
              />
            </div>

            {form.role === 'user' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Account Type</label>
                <Select
                  options={userTypeOptions}
                  value={userTypeOptions.find((opt) => opt.value === form.userType) || null}
                  onChange={handleUserTypeChange}
                  placeholder="Select account type"
                  isClearable
                  isDisabled={isViewMode}
                  className="w-full"
                  classNamePrefix="react-select"
                />
              </div>
            )}

            {form.role === 'user' && form.userType === 'company' ? (
              <FormInput
                label="Company Name"
                name="companyName"
                type="text"
                value={form.companyName}
                onChange={handleInputChange}
                required
                placeholder="Enter company name"
                disabled={isViewMode}
                className="md:col-span-2"
              />
            ) : (
              <>
                <FormInput
                  label="First Name"
                  name="firstName"
                  type="text"
                  value={form.firstName}
                  onChange={handleInputChange}
                  required={form.userType !== 'company'}
                  placeholder="Enter first name"
                  disabled={isViewMode}
                />
                <FormInput
                  label="Last Name"
                  name="lastName"
                  type="text"
                  value={form.lastName}
                  onChange={handleInputChange}
                  required={form.userType !== 'company'}
                  placeholder="Enter last name"
                  disabled={isViewMode}
                />
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gender</label>
              <Select
                options={genderOptions}
                value={genderOptions.find((opt) => opt.value === form.gender) || null}
                onChange={handleGenderChange}
                placeholder="Select gender"
                isClearable
                isDisabled={isViewMode}
                className="w-full"
                classNamePrefix="react-select"
              />
            </div>

            {isAddMode && (
              <FormInput
                label="Password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleInputChange}
                required
                placeholder="Enter password"
                disabled={isViewMode}
              />
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FaMapMarkerAlt className="text-blue-500" /> Contact Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country</label>
              <Select
                options={countryOptions}
                value={countryOptions.find((opt) => opt.value === form.country) || null}
                onChange={handleCountryChange}
                placeholder="Select a country"
                isClearable
                isDisabled={isViewMode}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">State</label>
              <Select
                options={stateOptions}
                value={stateOptions.find((opt) => opt.value === form.state) || null}
                onChange={handleStateChange}
                placeholder="Select a state"
                isClearable
                isDisabled={!form.country || isViewMode}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">City</label>
              <Select
                options={cityOptions}
                value={cityOptions.find((opt) => opt.value === form.city) || null}
                onChange={handleCityChange}
                placeholder="Select a city (optional)"
                isClearable
                isDisabled={!form.state || isViewMode}
              />
            </div>
            <FormInput label="Address" name="address" type="text" value={form.address} onChange={handleInputChange} required disabled={isViewMode} />
            <FormInput label="Street" name="street" type="text" value={form.street} onChange={handleInputChange} required disabled={isViewMode} />
            <FormInput label="Postal Code" name="postalCode" type="text" value={form.postalCode} onChange={handleInputChange} required disabled={isViewMode} />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
              <PhoneInput
                country={'us'}
                value={form.phone}
                onChange={handlePhoneChange}
                disabled={isViewMode}
                required
              />
            </div>
            <div>
              <FormInput
                label="Profile Picture (JPG/PNG)"
                name="profilePicture"
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleInputChange}
                disabled={isViewMode}
              />
            </div>
            {user?.profilePicture && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Profile Picture</label>
                <button
                  onClick={() => handleViewProfilePicture(user._id, user.profilePicture)}
                  className="mt-2 group"
                  type="button"
                >
                  <img
                    src={`${BASE_URL}${user.profilePicture}`}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600 shadow-md group-hover:ring-2 group-hover:ring-blue-500 transition-all duration-200"
                  />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Professional Details */}
        {form.role === 'specialist' && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <FaUser className="text-blue-500" /> Professional Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Fees (£)"
                name="fees"
                type="number"
                value={form.fees}
                onChange={handleInputChange}
                required
                placeholder="Enter fees"
                min="0"
                step="0.01"
                disabled={isViewMode}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-gray-100 transition-all duration-200 focus:border-blue-500"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Availability</label>
                <Select
                  options={availabilityOptions}
                  value={availabilityOptions.find((opt) => opt.value === form.availability) || null}
                  onChange={handleAvailabilityChange}
                  placeholder="Select availability"
                  isClearable
                  isDisabled={isViewMode}
                  className="w-full text-gray-900 dark:text-gray-100"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#d1d5db',
                      backgroundColor: '#fff',
                      borderRadius: '0.5rem',
                      padding: '0.25rem',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#3b82f6' },
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#fff',
                      borderRadius: '0.5rem',
                      marginTop: '0.25rem',
                    }),
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time Slots</label>
                <Select
                  options={timeSlotOptions}
                  value={timeSlotOptions.filter((opt) => form.timeSlots.includes(opt.value))}
                  onChange={handleTimeSlotsChange}
                  isMulti
                  placeholder="Select available time slots"
                  isClearable
                  isDisabled={isViewMode}
                  className="w-full text-gray-900 dark:text-gray-100"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#d1d5db',
                      backgroundColor: '#fff',
                      borderRadius: '0.5rem',
                      padding: '0.25rem',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#3b82f6' },
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#fff',
                      borderRadius: '0.5rem',
                      marginTop: '0.25rem',
                    }),
                  }}
                />
                {form.timeSlots.length > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Selected: {form.timeSlots.length} slot{form.timeSlots.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>

              <FormInput
                label="Experience (Years)"
                name="experience"
                type="number"
                value={form.experience}
                onChange={handleInputChange}
                required
                placeholder="Enter years of experience"
                min="0"
                max="50"
                disabled={isViewMode}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-gray-100 transition-all duration-200 focus:border-blue-500"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Specialties</label>
                <Select
                  options={specialtyOptions}
                  value={specialtyOptions.filter((opt) => form.specialties.includes(opt.value))}
                  onChange={handleSpecialtyChange}
                  isMulti
                  placeholder="Select up to 5 specialties"
                  isClearable
                  isDisabled={isViewMode}
                  className="w-full text-gray-900 dark:text-gray-100"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#d1d5db',
                      backgroundColor: '#fff',
                      borderRadius: '0.5rem',
                      padding: '0.25rem',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#3b82f6' },
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: '#fff',
                      borderRadius: '0.5rem',
                      marginTop: '0.25rem',
                    }),
                  }}
                  maxMenuHeight={200}
                />
                {form.specialties.length > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Selected: {form.specialties.length} / 5
                  </p>
                )}
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bio</label>
                <textarea
                  name="bio"
                  value={form.bio}
                  onChange={handleInputChange}
                  placeholder="Enter bio or about yourself"
                  disabled={isViewMode}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-gray-100 transition-all duration-200 focus:border-blue-500"
                  rows="5"
                />
              </div>
            </div>
          </div>
        )}

        {/* Documents and Verification */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FaFileAlt className="text-blue-500" /> Documents and Verification
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Document Type</label>
              <Select
                options={documentTypeOptions}
                value={documentTypeOptions.find((opt) => opt.value === form.documentType) || null}
                onChange={handleDocumentTypeChange}
                placeholder="Select document type"
                isClearable
                isDisabled={isViewMode}
              />
            </div>
            <div>
              <FormInput
                label="Upload Documents (PDF/JPG/PNG)"
                name="documents"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleInputChange}
                multiple
                disabled={isViewMode}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Active</label>
              <input
                type="checkbox"
                name="active"
                checked={form.active}
                onChange={handleInputChange}
                disabled={isViewMode}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Verified</label>
              <input
                type="checkbox"
                name="isVerified"
                checked={form.isVerified}
                onChange={handleInputChange}
                disabled={isViewMode}
              />
            </div>
          </div>

          {user?.verificationDocuments?.length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Documents</label>
              <div className="flex flex-wrap gap-3 mt-2">
                {user.verificationDocuments.map((doc) => (
                  <div
                    key={doc._id}
                    className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 p-2 rounded-lg shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                  >
                    <button
                      onClick={() => handleViewProfilePicture(user._id, doc.url)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center"
                      type="button"
                    >
                      <FaEye className="mr-1" /> {doc.type}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4 mt-8">
          <button
            type="button"
            onClick={() => navigate('/users')}
            className="px-6 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200"
          >
            Back
          </button>
          {!isViewMode && (
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg shadow-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : isAddMode ? 'Add User' : 'Update User'}
            </button>
          )}
        </div>
      </form>

      {/* Admin Password Change */}
      {isEditMode && authUser?.role === 'admin' && (
        <div className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FaLock className="text-blue-500" /> Change User Password
          </h3>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="New Password"
                name="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={handlePasswordInputChange}
                required
                placeholder="Enter new password"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-gray-100 transition-all duration-200 focus:border-blue-500"
              />
              <FormInput
                label="Confirm New Password"
                name="confirmNewPassword"
                type="password"
                value={passwordForm.confirmNewPassword}
                onChange={handlePasswordInputChange}
                required
                placeholder="Confirm new password"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-gray-100 transition-all duration-200 focus:border-blue-500"
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              New password must be at least 8 characters long.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handlePasswordChange}
                disabled={isPasswordLoading}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg shadow-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isPasswordLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManage;
